// Response Interceptor - Handles error responses, logging, and token refresh

import axios from 'axios';
import { sanitizeForLogging } from '../config/security.config';
import { API_RETRY_CONFIG } from '../config/api.config';

let tokenRefresher = null;
let logoutHandler = null;

// Set the token refresh function
export const setTokenRefresher = (refresher) => {
  if (typeof refresher !== 'function') {
    console.error('[Response Interceptor] Token refresher must be a function');
    return;
  }
  tokenRefresher = refresher;
};

// Set the logout handler function
export const setLogoutHandler = (handler) => {
  if (typeof handler !== 'function') {
    console.error('[Response Interceptor] Logout handler must be a function');
    return;
  }
  logoutHandler = handler;
};

// Calculate request duration for performance tracking
const getRequestDuration = (config) => {
  if (config?.metadata?.startTime) {
    return new Date().getTime() - config.metadata.startTime;
  }
  return 0;
};

// Response success interceptor - applied to every successful response
export const responseSuccessInterceptor = (response) => {
  const duration = getRequestDuration(response.config);

  if (process.env.NODE_ENV === 'development') {
    // console.log('[API Response]', {
    //   status: response.status,
    //   url: response.config?.url,
    //   duration: `${duration}ms`,
    // });
  }

  // Performance warning for slow requests
  if (duration > 3000) {
    console.warn('[API Performance] Slow request detected:', {
      url: response.config?.url,
      duration: `${duration}ms`,
    });
  }

  return response;
};

// Error message mapping
const ERROR_MESSAGES = {
  400: 'Invalid request. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  408: 'Request timeout. Please try again.',
  409: 'A conflict occurred. The resource may have been modified.',
  422: 'Validation failed. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An unexpected error occurred. Please try again later.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
  504: 'Request timeout. Please try again.',
  default: 'An unexpected error occurred. Please try again.',
};

const getErrorMessage = (status) => {
  return ERROR_MESSAGES[status] || ERROR_MESSAGES.default;
};

const isRetryableError = (status) => {
  return API_RETRY_CONFIG.retryableStatuses.includes(status);
};

// Response error interceptor - handles all API error responses
export const responseErrorInterceptor = async (error) => {
  const { config, response } = error;
  const duration = getRequestDuration(config);

  console.error('[API Error]', {
    status: response?.status,
    url: config?.url,
    duration: `${duration}ms`,
    message: error.message,
  });

  // Handle 401 - Unauthorized (Token expired)
  if (response?.status === 401) {
    if (tokenRefresher && config && !config._retry) {
      config._retry = true;

      try {
        // Refresh the token
        const newToken = await tokenRefresher();
        
        // If we got a new token, retry the original request
        if (newToken) {
          // Update the Authorization header with the new token
          config.headers.Authorization = `Bearer ${newToken}`;
          
          // Retry the request with the new token using a fresh axios instance
          // to avoid going through the same interceptors that might re-add old token
          return axios.request(config);
        }
        
        // If no new token was returned, reject and let the app handle it
        return Promise.reject(error);
      } catch (refreshError) {
        console.error('[Response Interceptor] Token refresh failed:', refreshError);
        if (logoutHandler) {
          logoutHandler();
        }
        return Promise.reject(error);
      }
    } else if (logoutHandler) {
      logoutHandler();
    }
  }

  // Create sanitized error object
  const sanitizedError = {
    status: response?.status || 0,
    message: getErrorMessage(response?.status),
    isRetryable: isRetryableError(response?.status),
    timestamp: new Date().toISOString(),
    requestId: response?.headers?.['x-request-id'] || null,
    ...(process.env.NODE_ENV === 'development' && {
      originalMessage: error.message,
      url: config?.url,
    }),
  };

  error.sanitizedError = sanitizedError;

  return Promise.reject(error);
};

export default {
  setTokenRefresher,
  setLogoutHandler,
  responseSuccessInterceptor,
  responseErrorInterceptor,
};
